<template>
  <BaseModal
    :is-visible="showModal"
    title="Create Deposit"
    @close="closeModal"
  >
    <template #default>
      <div
        v-if="!haveError && !haveRequest && !haveSuccess"
        :class="$style.box"
      >
        <div :class="$style.input__group">
          <label for="user">User</label>
          <select v-model="selectedUser" @change="selectError = false">
            <option value="" disabled selected>Select</option>
            <option v-for="user in users" :key="user" :value="user">
              {{ user }}
            </option>
          </select>
          <span
            v-if="selectError"
            :class="$style['error-message']"
          >
            Select a user
          </span>
        </div>

        <div :class="$style.input__group">
          <label for="amountDeposit">Deposit</label>
          <Money3Component
            v-model="amountDeposit"
            v-bind="configInput"
            :class="{ [$style['error']]: inputError }"
            @input="inputError = false"
          />

          <span
            v-if="inputError"
            :class="$style['error-message']"
          >
            {{ errorMessage }}
          </span>
        </div>
      </div>

      <div v-if="haveRequest">
        <div style="display: flex; justify-content: center; margin-bottom: 15px;">
          <LoadingSpinner />
        </div>
      </div>

      <div v-if="haveError">
        <div :class="[$style['box-message'], $style['error']]">
          <ExclamationTriangleIcon :class="$style.icon" />
          <span>
            We encountered an internal issue.<br />
            Please try again later.
          </span>
        </div>
      </div>

      <div v-if="haveSuccess">
        <div :class="[$style['box-message'], $style['success']]">
          <CheckCircleIcon :class="$style.icon" />
          <span>Deposit successfully created!</span>
        </div>
      </div>
    </template>

    <template #footer v-if="!haveError && !haveRequest && !haveSuccess">
      <button @click="create" :class="$style['btn-create']">Create</button>
    </template>
  </BaseModal>
</template>

<script setup>
  import { ref, watch, computed, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { Money3Component } from 'v-money3';
  import BaseModal from '@/components/common/BaseModal.vue';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import {
    ExclamationTriangleIcon,
    CheckCircleIcon
  } from '@heroicons/vue/24/outline';
import modalsService from '@/services/modalsService';

  const props = defineProps({
    showModal: {
      type: Boolean,
      default: false,
    },
  });

  const store = useStore();
  const emit = defineEmits(['close']);
  const amountDeposit = ref(0);
  const inputError = ref(false);
  const errorMessage = ref('Invalid amount');
  const selectError = ref(false);
  const users = ref([]);
  const selectedUser = ref('');
  
  const request = ref({
    pending: false,
    success: false,
    error: false
  });

  const configInput = ref({
    masked: false,
    prefix: '$',
    suffix: '',
    thousands: ',',
    decimal: '.',
    precision: 2,
    disableNegative: true,
    disabled: false,
    min: null,
    max: null,
    allowBlank: false,
    minimumNumberOfCharacters: 0,
    shouldRound: false,
    focusOnRight: false,
  });

  const haveSuccess = computed(() => request.value.success);
  const haveError = computed(() => request.value.error);
  const haveRequest = computed(() => request.value.pending);

  function closeModal() {
    store.dispatch('modals/hideModalDeposit');
    resetData();
    emit('close');
  }

  function resetData() {
    selectedUser.value = '';
    amountDeposit.value = 0;
    selectError.value = false;
    inputError.value = false;
  }

  function setErrorMessage() {
    errorMessage.value = 'Invalid amount';
  }

  async function create() {
    if (amountDeposit.value <= 0 || !selectedUser.value) {
      if(amountDeposit.value <= 0) {
        inputError.value = true;
        setErrorMessage();
      }

      if (!selectedUser.value) {
        selectError.value = true;
      }
      return
    }

    request.value.pending = true;

    try {

      const created = await modalsService.createDeposit(selectedUser.value, amountDeposit.value)

      if (created) {
        request.value.success = true;
        setTimeout(() => {
          resetData();
          request.value.success = false;
        }, 1500);
      }
      else request.value.error = true;

    } catch {
      request.value.error = true;
    }

    request.value.pending = false;
  }

  async function getUsers() {
    request.value.pending = true;

    try {
      const response = await modalsService.getUsers();
      users.value = response.users
    } catch {
      request.value.error = true;
    }

    request.value.pending = false;
  }

  async function initialize() {
    request.value = { pending: false, success: false, error: false };
    amountDeposit.value = 0;
    await getUsers();
  }

  onMounted(async () => {
    await initialize()
  });

  watch(() => props.showModal, (nv) => {
    console.log(nv)
    if (nv) initialize();
  });
</script>


<style lang="scss" module>

  .btn-create {
    border: unset;
    font-size: 16px;
    cursor: pointer;
    color: #232323;
    font-weight: 400;
    line-height: 26px;
    padding: 6px 16px;
    border-radius: 8px;    
    font-style: normal;
    margin-bottom: 5px;
    background: #BBF90A;
    font-family: $font-secondary;

    &:hover {
      color: #232323;
      background: #90C300;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  .error-message {
    color: #E64545;
    font-size: 12px;
  }

  .info-message {
    font-size: 12px;
  }

  .box-message {
    gap: 20px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .icon {
      width: 60px;
    }

    &.error {
      color:#E64545;
      
      .icon {
        color:#E64545
      }
    }

    &.success {
      color:#7CF95C;
      
      .icon {
        color:#7CF95C
      }
    }
  }

  .box {
    gap: 15px;
    display: flex;
    flex-direction: column;
  }

  .input__group {
    display: flex;
    font-size: 14px;
    flex-direction: column;

    label {
      color: #d9d9d9;
      margin-bottom: 4px;
    }

    select,
    input {
      padding: 8px;
      height: 45px;
      color: #fff;
      font-size: 16px;
      border-radius: 6px;
      border: 1px solid #535353;
      background: rgba(217, 217, 217, 0.05);
    }

    input {
      height: 27px;
    }
  }

</style>
